import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocale } from "../../context/LocaleContext";
import Seo from "../../components/Seo";
import { LEGAL, LEGAL_SLUGS, LEGAL_UPDATED, LEGAL_UPDATED_LABEL, LEGAL_DRAFT_NOTE } from "./legalContent";

export default function LegalPage({ doc }) {
  const { locale, setPageAlternates } = useLocale();
  const navigate = useNavigate();
  const data = LEGAL[doc];
  const title = data.title[locale] || data.title.en;
  const sections = data.sections[locale] || data.sections.en;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const slugs = LEGAL_SLUGS[doc];

  // Keep the URL on the locale-correct slug (e.g. /nl/terms -> /nl/voorwaarden)
  // so it matches the canonical and avoids duplicate URLs after a locale switch.
  useEffect(() => {
    const expected = `/${locale}/${slugs[locale]}`;
    if (typeof window !== "undefined" && window.location.pathname !== expected) {
      navigate(expected, { replace: true });
    }
  }, [locale, slugs, navigate]);

  useEffect(() => {
    setPageAlternates({
      en: `/en/${slugs.en}`, es: `/es/${slugs.es}`, nl: `/nl/${slugs.nl}`,
    });
  }, [doc, setPageAlternates, slugs]);

  const seo = {
    title: `${title} | LIVYVIO`,
    meta_description: title,
    canonical: `${origin}/${locale}/${slugs[locale]}`,
    hreflang: [
      { locale: "en", href: `${origin}/en/${slugs.en}` },
      { locale: "es", href: `${origin}/es/${slugs.es}` },
      { locale: "nl", href: `${origin}/nl/${slugs.nl}` },
      { locale: "x-default", href: `${origin}/en/${slugs.en}` },
    ],
    open_graph: { title, description: title, type: "website", site_name: "LIVYVIO" },
  };

  return (
    <>
      <Seo seo={seo} />
      <div className="container-page py-12 sm:py-16" data-testid={`legal-${doc}`}>
        <div className="mx-auto max-w-3xl">
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 sm:text-4xl" data-testid="legal-title">{title}</h1>
          <p className="mt-2 text-sm text-slate-400">{LEGAL_UPDATED_LABEL[locale]}: {LEGAL_UPDATED}</p>
          <p className="mt-4 rounded-xl border border-amber-100 bg-amber-50 p-4 text-xs leading-relaxed text-amber-800" data-testid="legal-draft-note">
            {LEGAL_DRAFT_NOTE[locale]}
          </p>
          <div className="mt-8 space-y-8">
            {sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-heading text-xl font-bold text-slate-900">{s.h}</h2>
                {s.p.map((para, j) => (
                  <p key={j} className="mt-2 text-sm leading-relaxed text-slate-600">{para}</p>
                ))}
              </section>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
